/**
 * SkillCard - Skills marketplace kart component'i
 */
import {
    BarChart3, Check, Code2, Download,
    FileSearch, FileText, Globe, MessageSquare,
    Sparkles, Star, TestTube, Zap
} from 'lucide-react';
import type { Skill } from '../hooks/useSkills';

const ICON_MAP: Record<string, React.ReactNode> = {
  Code2: <Code2 size={20} />,
  BarChart3: <BarChart3 size={20} />,
  TestTube: <TestTube size={20} />,
  FileSearch: <FileSearch size={20} />,
  FileText: <FileText size={20} />,
  Globe: <Globe size={20} />,
  Sparkles: <Sparkles size={20} />,
  Zap: <Zap size={20} />,
}

function SkillIcon({ iconName, color }: { iconName?: string; color?: string }) {
  const icon = ICON_MAP[iconName ?? 'Zap'] ?? <Zap size={20} />
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '10px',
      background: `${color ?? '#3b82f6'}22`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: color ?? '#3b82f6', flexShrink: 0
    }}>
      {icon}
    </div>
  )
}

interface SkillCardProps {
  skill: Skill
  installing: number | null
  onInstall: (id: number, e: React.MouseEvent) => void
  onRun?: (id: number, e: React.MouseEvent) => void
  onClick: () => void
  featured?: boolean
}

export default function SkillCard({ skill, installing, onInstall, onRun, onClick, featured }: SkillCardProps) {
  const borderColor = featured ? `${skill.color ?? '#3b82f6'}44` : 'var(--border-color)'

  return (
    <div
      onClick={onClick}
      style={{
        padding: '16px', background: 'var(--card-bg)',
        border: `1px solid ${borderColor}`,
        borderRadius: '12px', cursor: 'pointer', transition: 'all 0.15s',
        position: 'relative', overflow: 'hidden'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = skill.color ?? '#3b82f6'
        e.currentTarget.style.transform = 'translateY(-1px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = borderColor
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* Badges */}
      <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '4px' }}>
        {skill.is_new && (
          <span style={{ fontSize: '10px', padding: '2px 7px', background: '#10b981', color: '#fff', borderRadius: '4px', fontWeight: 700 }}>NEW</span>
        )}
        {featured && (
          <span style={{ fontSize: '10px', padding: '2px 7px', background: '#f59e0b', color: '#fff', borderRadius: '4px', fontWeight: 700 }}>FEATURED</span>
        )}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
        <SkillIcon iconName={skill.icon_name} color={skill.color} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {skill.name}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            by {skill.author} · v{skill.version}
          </div>
        </div>
      </div>

      {/* Description */}
      <p style={{
        fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: 1.5,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
      } as React.CSSProperties}>
        {skill.description}
      </p>

      {/* Category badge */}
      <div style={{ marginBottom: '12px' }}>
        <span style={{
          fontSize: '11px', padding: '2px 8px',
          background: `${skill.color ?? '#3b82f6'}18`,
          color: skill.color ?? '#3b82f6',
          borderRadius: '4px', fontWeight: 600
        }}>
          {skill.category}
        </span>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <Star size={11} color="#f59e0b" fill="#f59e0b" /> {skill.rating.toFixed(1)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <Download size={11} /> {skill.downloads.toLocaleString()}
          </span>
          {(skill.discussion_count ?? 0) > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <MessageSquare size={11} /> {skill.discussion_count}
            </span>
          )}
        </div>

        {skill.is_installed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
              <Check size={12} /> Installed
            </span>
            {onRun && (
              <button
                onClick={e => { void onRun(skill.id, e) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '5px 10px', background: skill.color ?? '#3b82f6',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                }}
              >
                <Zap size={11} /> Run
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={e => { void onInstall(skill.id, e) }}
            disabled={installing === skill.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 12px', background: skill.color ?? '#3b82f6',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: installing === skill.id ? 'not-allowed' : 'pointer',
              fontSize: '12px', fontWeight: 600,
              opacity: installing === skill.id ? 0.6 : 1
            }}
          >
            <Download size={12} /> {installing === skill.id ? '...' : 'Install'}
          </button>
        )}
      </div>
    </div>
  )
}
